/**
 * Iter124 — Dashboard Case Map.
 *
 * Google Maps view centered on the USA that renders one pin per case with a
 * county assignment (case has `latitude`+`longitude` OR county centroid).
 * Auto-refreshes every 30s so pins update when a county assignment is
 * added or changed in real time. Click a pin → InfoWindow with case ID
 * + title + status; the whole card links to the case.
 */
import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { BACKEND_URL } from '../config/canonical';
import { MapPin as MapPinIcon } from 'lucide-react';

const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
const USA_CENTER = { lat: 39.8283, lng: -98.5795 }; // geographic centre of contiguous US
const USA_ZOOM = 4;

let _gmapsLoadingPromise = null;
function loadGoogleMaps() {
  if (typeof window === 'undefined') return Promise.reject(new Error('SSR'));
  if (window.google && window.google.maps) return Promise.resolve(window.google);
  if (_gmapsLoadingPromise) return _gmapsLoadingPromise;
  _gmapsLoadingPromise = new Promise((resolve, reject) => {
    const s = document.createElement('script');
    s.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAPS_API_KEY}&libraries=marker`;
    s.async = true; s.defer = true;
    s.onload = () => resolve(window.google);
    s.onerror = () => reject(new Error('Failed to load Google Maps'));
    document.head.appendChild(s);
  });
  return _gmapsLoadingPromise;
}

const STATUS_COLORS = {
  NEW: '#10B981', ACTIVE: '#F59E0B', ASSIGNED: '#3B82F6',
  UNDER_REVIEW: '#8B5CF6', COMPLETED: '#22D3EE',
  CLOSED: '#64748B', PENDING_REVIEW: '#0EA5E9',
};

export default function DashboardCaseMap() {
  const mapDivRef = useRef(null);
  const mapRef = useRef(null);
  const markersRef = useRef([]);
  const infoRef = useRef(null);
  const [cases, setCases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mapReady, setMapReady] = useState(false);

  const token = typeof window !== 'undefined' ? localStorage.getItem('nexus_token') : null;

  const fetchCases = useCallback(async () => {
    if (!token) return;
    try {
      const r = await fetch(`${BACKEND_URL}/api/cases`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!r.ok) return;
      const data = await r.json();
      setCases(Array.isArray(data) ? data : []);
    } finally { setLoading(false); }
  }, [token]);

  // Poll every 30s so county changes reflect quickly
  useEffect(() => {
    fetchCases();
    const t = setInterval(fetchCases, 30000);
    return () => clearInterval(t);
  }, [fetchCases]);

  // Init map
  useEffect(() => {
    let cancelled = false;
    if (!GOOGLE_MAPS_API_KEY) { setMapReady(false); return; }
    loadGoogleMaps().then(google => {
      if (cancelled || !mapDivRef.current) return;
      mapRef.current = new google.maps.Map(mapDivRef.current, {
        center: USA_CENTER, zoom: USA_ZOOM, mapTypeControl: true,
        streetViewControl: false, fullscreenControl: false,
        styles: [
          { elementType: 'geometry', stylers: [{ color: '#0F1B2D' }] },
          { elementType: 'labels.text.fill', stylers: [{ color: '#94A3B8' }] },
          { elementType: 'labels.text.stroke', stylers: [{ color: '#0B1220' }] },
          { featureType: 'water', elementType: 'geometry', stylers: [{ color: '#0B1220' }] },
          { featureType: 'administrative', elementType: 'geometry.stroke', stylers: [{ color: '#334155' }] },
        ],
      });
      infoRef.current = new google.maps.InfoWindow();
      setMapReady(true);
    }).catch(() => setMapReady(false));
    return () => { cancelled = true; };
  }, []);

  // Filter cases with a location
  const geoCases = useMemo(
    () => (cases || []).filter(c => typeof c.latitude === 'number' && typeof c.longitude === 'number'),
    [cases],
  );

  // Repaint markers when cases change
  useEffect(() => {
    if (!mapReady || !mapRef.current || !window.google) return;
    const google = window.google;
    // Clear previous
    markersRef.current.forEach(m => m.setMap(null));
    markersRef.current = [];
    geoCases.forEach(c => {
      const marker = new google.maps.Marker({
        position: { lat: c.latitude, lng: c.longitude },
        map: mapRef.current,
        title: c.title || c.case_number || c.id,
        icon: {
          path: google.maps.SymbolPath.CIRCLE, scale: 8,
          fillColor: STATUS_COLORS[c.status] || '#F59E0B', fillOpacity: 0.9,
          strokeColor: '#0F172A', strokeWeight: 2,
        },
      });
      marker.addListener('click', () => {
        const content = `
          <div style="font-family:Inter,sans-serif;color:#111;min-width:200px;">
            <div style="font-size:11px;color:#64748B;text-transform:uppercase;letter-spacing:0.08em;font-weight:700;">${c.case_number || ''}</div>
            <div style="font-size:14px;font-weight:700;margin-top:2px;">${c.title || 'Untitled case'}</div>
            <div style="font-size:11px;color:#334155;margin-top:4px;">${c.county ? c.county + ' County · ' : ''}${c.state || ''}</div>
            <div style="font-size:11px;color:${STATUS_COLORS[c.status] || '#F59E0B'};font-weight:700;margin-top:4px;">${(c.status || '').replace(/_/g, ' ')}</div>
            <a href="/cases/${c.id}" style="display:inline-block;margin-top:6px;font-size:11px;color:#F59E0B;text-decoration:underline;">Open case →</a>
          </div>`;
        infoRef.current.setContent(content);
        infoRef.current.open({ anchor: marker, map: mapRef.current });
      });
      markersRef.current.push(marker);
    });
    // Fit bounds if we have multiple pins in a small area (>1 case, span ≤ 8°)
    if (geoCases.length >= 2) {
      const bounds = new google.maps.LatLngBounds();
      geoCases.forEach(c => bounds.extend({ lat: c.latitude, lng: c.longitude }));
      const span = bounds.toSpan();
      if (span.lat() < 8 && span.lng() < 12) {
        mapRef.current.fitBounds(bounds, 60);
      } else {
        mapRef.current.setCenter(USA_CENTER);
        mapRef.current.setZoom(USA_ZOOM);
      }
    } else {
      mapRef.current.setCenter(USA_CENTER);
      mapRef.current.setZoom(USA_ZOOM);
    }
  }, [geoCases, mapReady]);

  return (
    <div className="nx-panel" data-testid="dashboard-case-map" style={{ padding: 12, marginBottom: 18 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <MapPinIcon size={16} style={{ color: '#F59E0B' }} />
          <div>
            <div className="nx-eyebrow">Case Map</div>
            <div style={{ fontSize: 14, fontWeight: 700, color: '#F8FAFC' }}>
              County Assignments · {geoCases.length} pin{geoCases.length === 1 ? '' : 's'}
            </div>
          </div>
        </div>
        <div className="nx-mono" style={{ fontSize: 10, color: '#94A3B8', letterSpacing: '0.10em', textTransform: 'uppercase' }}>
          Auto-refreshes every 30s
        </div>
      </div>
      {!GOOGLE_MAPS_API_KEY ? (
        <div style={{ height: 380, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#94A3B8', fontSize: 13 }}>
          Google Maps API key not configured.
        </div>
      ) : (
        <div ref={mapDivRef} style={{ height: 420, width: '100%', borderRadius: 10, overflow: 'hidden', background: '#0F1B2D' }} />
      )}
      {loading && (
        <div style={{ marginTop: 6, fontSize: 10, color: '#94A3B8' }}>Loading cases…</div>
      )}
      {!loading && geoCases.length === 0 && (
        <div style={{ marginTop: 6, fontSize: 12, color: '#94A3B8' }}>
          No cases have a county assignment yet. Add State → City → County in the case detail to see them here.
        </div>
      )}
    </div>
  );
}
